import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom'; // <-- Imported for tracking navigation transfer state
import * as Icons from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { leadsApi, propertiesApi } from '../api/endpoints';
import { useAuth } from '../context/AuthContext';
import { DeleteButton } from '../components/DeleteButton';
import { LeadActivityDrawer, buildWhatsappLink, buildTelLink } from '../components/LeadActivityDrawer';
import LeadImportDialog from '../components/LeadImportDialog';
import PrintLeadsDialog from '../components/PrintLeadsDialog';
import RecordingDialog from '../components/RecordingDialog';
import { activitiesApi } from '../api/endpoints';
import { toast } from 'sonner';
import { useTeamOptions } from '../hooks/useTeamOptions';

const priorityColor = {
  Hot: 'bg-rose-50 text-rose-700 border-rose-200',
  Interested: 'bg-amber-50 text-amber-700 border-amber-200',
  Average: 'bg-sky-50 text-sky-700 border-sky-200',
  // Legacy values still rendered with sensible colours just in case
  Warm: 'bg-amber-50 text-amber-700 border-amber-200',
  Cold: 'bg-sky-50 text-sky-700 border-sky-200',
};

const statusColor = {
  New: 'bg-slate-100 text-slate-700',
  Contacted: 'bg-blue-100 text-blue-700',
  'Scheduled Site Visits': 'bg-green-100 text-green-700',
  'Avg Customer Site Visit': 'bg-purple-100 text-purple-700',
  'Site Visit Completed': 'bg-sky-100 text-sky-700',
  Negotiation: 'bg-violet-100 text-violet-700',
  Booked: 'bg-emerald-100 text-emerald-700',
  Lost: 'bg-rose-100 text-rose-700',
};

const PRIORITY_OPTIONS = ['Hot', 'Interested', 'Average'];
const PROFESSION_OPTIONS = ['Doctor', 'Engineer', 'Advocate', 'CA', 'Govt Job', 'Pvt Job', 'Other'];
const STATUS_OPTIONS = ['New', 'Contacted', 'Scheduled Site Visits', 'Avg Customer Site Visit', 'Site Visit Completed', 'Negotiation', 'Booked', 'Lost'];
const SOURCE_OPTIONS = ['Meta (Facebook)', 'Instagram', 'Google Ads', 'WhatsApp Campaign', 'Other Ads', 'Website', 'Referral', 'Walk-in', 'Magicbricks', '99acres'];

// Custom debounce hook to prevent excessive API calls on keystrokes
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const LeadFormDialog = React.memo(({ properties = [], onSaved, trigger, initial }) => {
  const isEdit = !!initial;
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', alternate_mobile_number: '', source: 'Website',
    property: '', priority: 'Interested', budget: '', profession: '',
    status: 'New', owner: '', notes: '',
  });
  const team = useTeamOptions();

  useEffect(() => {
    if (open) {
      setForm({
        name: initial?.name || '',
        email: initial?.email || '',
        phone: initial?.phone || '',
        alternate_mobile_number: initial?.alternate_mobile_number || '',
        profession: initial?.profession || '',
        source: initial?.source || 'Website',
        property: initial?.property || (properties.length > 0 ? properties[0].name : ''),
        priority: initial?.priority || 'Interested',
        budget: initial?.budget || '',
        status: initial?.status || 'New',
        owner: initial?.owner || '',
        notes: initial?.notes || '',
      });
    }
  }, [open, initial, properties]);

  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name?.trim() || !form.phone?.trim()) { 
      toast.error('Name and phone are required'); 
      return; 
    }
    if (form.alternate_mobile_number?.trim()) {
      const digits = form.alternate_mobile_number.replace(/\D/g, '');
      if (digits.length < 10 || digits.length > 15) {
        toast.error('Alternate mobile number must contain 10-15 digits');
        return;
      }
    }
    setSaving(true);
    try {
      const saved = isEdit
        ? await leadsApi.update(initial.id, form)
        : await leadsApi.create(form);
      onSaved(saved, isEdit);
      toast.success(isEdit ? 'Lead updated' : 'Lead created');
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save lead');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">{isEdit ? 'Edit Lead' : 'Create New Lead'}</DialogTitle>
          <DialogDescription>{isEdit ? 'Update details for this prospect.' : 'Capture an enquiry into the Kiana Developers pipeline.'}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2"><Label className="font-bold uppercase text-[11px]">Full Name *</Label><Input value={form.name} onChange={e => update('name', e.target.value)} /></div>
          <div><Label className="font-bold uppercase text-[11px]">Email</Label><Input type="email" value={form.email} onChange={e => update('email', e.target.value)} /></div>
          <div><Label className="font-bold uppercase text-[11px]">Phone *</Label><Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="+91 9561774777" /></div>
          <div><Label className="font-bold uppercase text-[11px]">Alternate Mobile</Label><Input value={form.alternate_mobile_number} onChange={e => update('alternate_mobile_number', e.target.value)} placeholder="+91 9876543210" data-testid="lead-alt-mobile-input" /></div>
          <div>
            <Label className="font-bold uppercase text-[11px]">Profession</Label>
            <Select value={form.profession} onValueChange={v => update('profession', v)}>
              <SelectTrigger data-testid="lead-profession-select"><SelectValue placeholder="Select Profession" /></SelectTrigger>
              <SelectContent>
                {PROFESSION_OPTIONS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-bold uppercase text-[11px]">Source</Label>
            <Select value={form.source} onValueChange={v => update('source', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {SOURCE_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-bold uppercase text-[11px]">Status</Label>
            <Select value={form.status} onValueChange={v => update('status', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-bold uppercase text-[11px]">Priority</Label>
            <Select value={form.priority} onValueChange={v => update('priority', v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="font-bold uppercase text-[11px]">Team Co-Ordinator</Label>
            <Select value={form.owner} onValueChange={v => update('owner', v)}>
              <SelectTrigger><SelectValue placeholder="Select Co-ordinator" /></SelectTrigger>
              <SelectContent>
                {team.map(u => <SelectItem key={u.id} value={u.name}>{u.name} — {u.role}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="font-bold uppercase text-[11px]">Interested Project</Label>
            <Select value={form.property} onValueChange={v => update('property', v)}>
              <SelectTrigger><SelectValue placeholder="Select Property" /></SelectTrigger>
              <SelectContent>
                {properties.map(p => <SelectItem key={p.id} value={p.name}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2"><Label className="font-bold uppercase text-[11px]">Budget</Label><Input value={form.budget} onChange={e => update('budget', e.target.value)} placeholder="e.g. ₹75 L" /></div>
          <div className="sm:col-span-2"><Label className="font-bold uppercase text-[11px]">Notes</Label><Input value={form.notes} onChange={e => update('notes', e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="bg-brand-navy hover:bg-[#13294A]" onClick={submit} disabled={saving}>
            {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Lead')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});
LeadFormDialog.displayName = 'LeadFormDialog';

const LazyRecordingButton = ({ lead }) => {
  const [recs, setRecs] = useState([]);
  const fetchRecs = useCallback(async () => {
    try {
      const acts = await activitiesApi.forLead(lead.id);
      setRecs(acts.filter(a => a?.metadata?.recording_url || (a.type === 'call' && a?.metadata?.recording_url)));
    } catch (e) { console.error('Failed to load call recordings', e); }
  }, [lead.id]);
  return (
    <RecordingDialog
      lead={lead}
      recordings={recs}
      onChanged={fetchRecs}
      trigger={
        <Button
          size="icon" variant="ghost" className="text-violet-600"
          title="Call Recordings"
          data-testid={`recording-btn-${lead.id}`}
          onClick={fetchRecs}
        >
          <Icons.Mic className="w-4 h-4" />
        </Button>
      }
    />
  );
};

const Leads = () => {
  const { hasPermission } = useAuth();
  const location = useLocation(); // <-- Checking dashboard cross-navigation transfer state
  
  const canCreate = hasPermission('leads', 'create') ?? true;
  const canEdit = hasPermission('leads', 'edit') ?? true;
  const canDelete = hasPermission('leads', 'delete') ?? true;
  const isAdmin = hasPermission('admin', 'view') ?? false;

  // Extract transfer filters cleanly from location redirect state
  const initialPriority = location.state?.filterPriority || 'all';
  const initialStatus = location.state?.filterStatus || 'all';
  const initialSource = location.state?.filterSource || 'all';
  const initialProject = location.state?.filterProject || '';

  const [data, setData] = useState([]);
  const [props, setProps] = useState([]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState(initialStatus);
  const [priority, setPriority] = useState(initialPriority);
  const [source, setSource] = useState(initialSource);
  const [idFilter, setIdFilter] = useState(location.state?.filterIds || null);
  const [projectFilter, setProjectFilter] = useState(initialProject);
  const filterLabel = location.state?.filterLabel;
  const [loading, setLoading] = useState(true);
  const [activeLead, setActiveLead] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [printOpen, setPrintOpen] = useState(false);

  const toggleSelect = useCallback((id) => {
    setSelected(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  }, []);

  const debouncedSearch = useDebounce(q, 300);

  const openLead = useCallback((l) => { 
    setActiveLead(l); 
    setDrawerOpen(true); 
  }, []);

  // Initial load structural data sequence
  useEffect(() => {
    let isMounted = true;
    const loadInitialData = async () => {
      try {
        const initialParams = {};
        if (initialPriority !== 'all') initialParams.priority = initialPriority;
        if (initialStatus !== 'all') initialParams.status = initialStatus;
        if (initialSource !== 'all') initialParams.source = initialSource;
        if (initialProject) initialParams.property = initialProject;

        const [list, p] = await Promise.all([
          leadsApi.list(initialParams), 
          propertiesApi.list().catch(() => [])
        ]);
        if (isMounted) {
          setData(list || []); 
          setProps(p || []);
        }
      } catch (e) { 
        toast.error('Failed to fetch initial structural data');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadInitialData();
    return () => { isMounted = false; };
  }, [initialPriority, initialStatus, initialSource, initialProject]);

  // Structural runtime tracking effect pipeline (Triggers dynamic payload queries)
  useEffect(() => {
    if (loading) return;

    const fetchFilteredData = async () => {
      try {
        const params = {};
        if (status !== 'all') params.status = status;
        if (priority !== 'all') params.priority = priority;
        if (source !== 'all') params.source = source;
        if (projectFilter) params.property = projectFilter;
        if (debouncedSearch) params.q = debouncedSearch;
        
        const response = await leadsApi.list(params);
        setData(response || []);
      } catch (e) {
        toast.error('Failed to filter leads');
      }
    };

    fetchFilteredData();
  }, [debouncedSearch, status, priority, source, projectFilter, loading]);

  const onSaved = useCallback((saved, isEdit) => {
    if (isEdit) setData(d => d.map(x => x.id === saved.id ? saved : x));
    else setData(d => [saved, ...d]);
  }, []);

  const onDelete = useCallback(async (l) => {
    try {
      await leadsApi.remove(l.id);
      setData(d => d.filter(x => x.id !== l.id));
      toast.success(`Deleted ${l.name}`);
    } catch (e) {
      toast.error('Could not delete lead.');
    }
  }, []);

  const visible = useMemo(() => {
    if (!idFilter) return data;
    const ids = new Set(idFilter);
    return data.filter(l => ids.has(l.id));
  }, [data, idFilter]);

  const owners = useMemo(() => [...new Set(data.map(l => l.owner).filter(Boolean))].sort(), [data]);
  const allVisibleSelected = visible.length > 0 && visible.every(l => selected.has(l.id));
  const toggleSelectAll = useCallback(() => {
    setSelected(prev => {
      if (visible.length && visible.every(l => prev.has(l.id))) return new Set();
      return new Set(visible.map(l => l.id));
    });
  }, [visible]);

  const stats = useMemo(() => {
    return [
      { label: 'Total Leads', value: data.length, icon: 'Users', color: 'text-brand-navy' },
      { label: 'Hot', value: data.filter(l => l.priority === 'Hot').length, icon: 'Flame', color: 'text-rose-600' },
      { label: 'Site Visits', value: data.filter(l => l.status === 'Site Visit Completed' || l.status === 'Scheduled Site Visits').length, icon: 'MapPin', color: 'text-amber-600' },
      { label: 'Booked', value: data.filter(l => l.status === 'Booked').length, icon: 'KeyRound', color: 'text-emerald-600' },
    ];
  }, [data]);

  const exportCsv = useCallback(() => {
    if (!visible.length) { toast.error('No leads to export'); return; }
    const cols = ['Lead ID', 'Name', 'Phone', 'Alternate Mobile', 'Email', 'Source', 'Status', 'Priority', 'Project', 'Budget', 'Profession', 'Coordinator', 'Created', 'Notes'];
    const esc = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = visible.map(l => [
      l.displayId, l.name, l.phone, l.alternate_mobile_number, l.email, l.source, l.status, l.priority,
      l.property, l.budget, l.profession, l.owner, l.createdAt, l.notes,
    ].map(esc).join(','));
    const csv = '\ufeff' + [cols.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `kiana-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success(`Exported ${visible.length} lead${visible.length === 1 ? '' : 's'} to CSV`);
  }, [visible]);

  const getInitials = (name) => {
    if (!name) return '??';
    return name
      .trim()
      .split(/\s+/)
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {stats.map((s) => {
          const Icon = Icons[s.icon];
          return (
            <Card key={s.label} className="border-slate-200 card-lift">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="text-[11px] text-slate-500 uppercase tracking-wide font-bold">{s.label}</div>
                  <div className="font-display text-2xl font-bold mt-1">{s.value}</div>
                </div>
                {Icon && <Icon className={`w-7 h-7 ${s.color}`} />}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-3 sm:p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Icons.Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search leads..." value={q} onChange={e => setQ(e.target.value)} />
          </div>
          
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[140px] sm:w-[160px] font-bold uppercase text-[11px]" data-testid="leads-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={source} onValueChange={setSource}>
            <SelectTrigger className="w-[140px] sm:w-[160px] font-bold uppercase text-[11px]" data-testid="leads-source-filter"><SelectValue placeholder="Source" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              {SOURCE_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <Select value={priority} onValueChange={setPriority}>
            <SelectTrigger className="w-[120px] sm:w-[140px] font-bold uppercase text-[11px]"><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Priority</SelectItem>
              {PRIORITY_OPTIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          {isAdmin && (
            <LeadImportDialog
              onImported={() => onSaved && onSaved(null, false)}
              trigger={
                <Button variant="outline" size="sm" data-testid="leads-import-btn">
                  <Icons.Upload className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Import</span>
                </Button>
              }
            />
          )}
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={exportCsv} data-testid="leads-export-btn">
              <Icons.Download className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline">Export</span>
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => setPrintOpen(true)} data-testid="leads-print-btn">
            <Icons.Printer className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">Print{selected.size ? ` (${selected.size})` : ''}</span>
          </Button>
          {canCreate && (
            <LeadFormDialog
              properties={props}
              onSaved={onSaved}
              trigger={
                <Button className="bg-brand-navy hover:bg-[#13294A] text-white" data-testid="new-lead-btn">
                  <Icons.Plus className="w-4 h-4 sm:mr-1.5" /><span className="hidden sm:inline font-bold">New Lead</span>
                </Button>
              }
            />
          )}
        </CardContent>
      </Card>

      {projectFilter && (
        <div className="flex items-center gap-2 bg-brand-gold-soft/40 border border-brand-gold/30 rounded-lg px-3 py-2" data-testid="leads-project-filter-banner">
          <Icons.Building2 className="w-4 h-4 text-brand-navy shrink-0" />
          <span className="text-sm font-medium text-slate-700 truncate">Project: {projectFilter}{filterLabel ? ` · ${filterLabel}` : ''}</span>
          <Button size="sm" variant="ghost" className="h-7 text-xs ml-auto shrink-0" onClick={() => setProjectFilter('')} data-testid="leads-clear-project-filter">
            <Icons.X className="w-3.5 h-3.5 mr-1" /> Clear
          </Button>
        </div>
      )}

      {idFilter && (
        <div className="flex items-center gap-2 bg-brand-navy/5 border border-brand-navy/15 rounded-lg px-3 py-2" data-testid="leads-id-filter-banner">
          <Icons.Filter className="w-4 h-4 text-brand-navy shrink-0" />
          <span className="text-sm font-medium text-slate-700 truncate">
            {filterLabel || 'Dashboard filter'} — {visible.length} lead{visible.length === 1 ? '' : 's'}
          </span>
          <Button size="sm" variant="ghost" className="h-7 text-xs ml-auto shrink-0" onClick={() => setIdFilter(null)} data-testid="leads-clear-id-filter">
            <Icons.X className="w-3.5 h-3.5 mr-1" /> Clear filter
          </Button>
        </div>
      )}

      <Card className="border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 font-bold">
                <th className="px-3 py-3 w-8">
                  <input type="checkbox" className="accent-[#1B3A5C] w-4 h-4 cursor-pointer" checked={allVisibleSelected} onChange={toggleSelectAll} data-testid="leads-select-all" />
                </th>
                <th className="px-4 py-3">Lead</th>
                <th className="px-4 py-3 hidden sm:table-cell">Project</th>
                <th className="px-4 py-3 hidden lg:table-cell">Alt. Mobile</th>
                <th className="px-4 py-3 hidden md:table-cell">Source</th>
                <th className="px-4 py-3 hidden lg:table-cell">Profession</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 hidden md:table-cell">Priority</th>
                <th className="px-4 py-3 hidden xl:table-cell">Team Co-Ordinator</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && Array.from({ length: 6 }).map((_, i) => (
                <tr key={i}><td colSpan={10} className="p-2"><Skeleton className="h-10 w-full" /></td></tr>
              ))}
              {!loading && visible.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center py-10 text-slate-400 bg-white font-medium">
                    No leads found matching your filters.
                  </td>
                </tr>
              )}
              {!loading && visible.map((l) => (
                <tr
                  key={l.id}
                  className="hover:bg-slate-50 transition-colors cursor-pointer"
                  onClick={() => openLead(l)}
                >
                  <td className="px-3 py-3 w-8" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" className="accent-[#1B3A5C] w-4 h-4 cursor-pointer" checked={selected.has(l.id)} onChange={() => toggleSelect(l.id)} data-testid={`lead-select-${l.id}`} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5 text-left">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="text-xs bg-brand-navy text-white">
                          {getInitials(l.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="font-bold text-slate-800 truncate hover:text-brand-navy flex items-center gap-1.5">
                          {l.displayId && <span className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{l.displayId}</span>}
                          <span>{l.name}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">{l.phone}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <div className="text-slate-800 truncate max-w-[160px] font-bold">{l.property}</div>
                    <div className="text-[11px] text-slate-500">{l.budget}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-600 hidden lg:table-cell text-[12px]" data-testid={`lead-alt-mobile-${l.id}`}>{l.alternate_mobile_number || '—'}</td>
                  <td className="px-4 py-3 text-slate-600 hidden md:table-cell">{l.source}</td>
                  <td className="px-4 py-3 text-slate-600 hidden lg:table-cell" data-testid={`lead-profession-${l.id}`}>{l.profession || '—'}</td>
                  <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded text-[11px] font-bold uppercase tracking-tight ${statusColor[l.status] || 'bg-slate-100'}`}>{l.status}</span></td>
                  <td className="px-4 py-3 hidden md:table-cell"><Badge variant="outline" className={`${priorityColor[l.priority] || ''} font-bold`}>{l.priority}</Badge></td>
                  <td className="px-4 py-3 text-slate-600 hidden xl:table-cell font-bold uppercase text-[11px]">{l.owner}</td>
                  <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-0.5">
                      <a href={buildTelLink(l.phone)}>
                        <Button size="icon" variant="ghost" title="Call"><Icons.Phone className="w-4 h-4" /></Button>
                      </a>
                      <a href={buildWhatsappLink(l.phone, `Hello ${l.name}, regarding ${l.property}`)} target="_blank" rel="noopener noreferrer">
                        <Button size="icon" variant="ghost" className="text-emerald-600" title="WhatsApp"><Icons.MessageCircle className="w-4 h-4" /></Button>
                      </a>
                      <LazyRecordingButton lead={l} />
                      <Button size="icon" variant="ghost" title="View activity" onClick={() => openLead(l)}>
                        <Icons.History className="w-4 h-4" />
                      </Button>
                      {canEdit && (
                        <div onClick={(e) => e.stopPropagation()}>
                          <LeadFormDialog
                            properties={props}
                            initial={l}
                            onSaved={onSaved}
                            trigger={<Button size="icon" variant="ghost" title="Edit"><Icons.Pencil className="w-4 h-4" /></Button>}
                          />
                        </div>
                      )}
                      {canDelete && (
                        <DeleteButton
                          title={`Delete ${l.name}?`}
                          description="This will permanently remove the lead and any related history."
                          onConfirm={() => onDelete(l)}
                        />
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <LeadActivityDrawer
        lead={activeLead}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onLeadUpdated={onSaved}
      />

      <PrintLeadsDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        selectedIds={[...selected]}
        filteredIds={visible.map(l => l.id)}
        projects={props.map(p => p.name)}
        owners={owners}
      />
    </div>
  );
};

export default Leads;